/**
 * O identificador enquanto se DIGITA: o h\u00edfen do fim fica \u2014 quem digitou "tarefas-" est\u00e1 no meio de
 * escrever "tarefas-abertas", e tir\u00e1-lo a cada tecla tornava o h\u00edfen imposs\u00edvel de digitar (relato do
 * usu\u00e1rio). Quem o tira \u00e9 o `aliasSuggestion`, ao sair do campo.
 */
export function incompleteAliasSuggestion(name: string): string {
    return name
        ? name
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^[^a-z0-9]+/, '')
            .replace(/--/g, '-')
            .trim()
        : ''
}

export function isNullOrUndefined(obj: any): obj is null | undefined {
    return obj === null || obj === undefined
}

/**
 * Estado marcado de uma propriedade booleana. Tolerante à string de propósito: o
 * backend não faz coerção de tipo nenhuma (o laço por propriedade do
 * Instance.fromPersistence está comentado), então o que foi gravado volta como está.
 */
export function asBoolean(value: any): boolean {
    return value === true || value === 'true'
}

export function aliasSuggestion(name: string): string {
    return incompleteAliasSuggestion(name).replace(/[^a-z0-9]$/g, '')
}

/**
 * O nome do atributo dentro de uma fórmula: o camelCase do alias (`data-do-prazo` →
 * `dataDoPrazo`), com os escapes do que não serviria como nome — `item-2` → `item_2`,
 * `2-turno` → `_2Turno`, `size` → `size_`.
 *
 * O alias é kebab, bom em URL e no banco e impossível numa expressão — o hífen é o sinal de menos.
 * A tela de design mostra este nome ao lado do alias desde que o atributo nasce, para o designer
 * conhecer o nome que vai escrever. Nenhum alias é recusado: o escape é o que torna todos usáveis.
 *
 * **Escrita duas vezes**, com a lista de reservados, como a escada de papéis: o `celName` do
 * servidor (`formula/names.ts`) é o dono, e é o nome dele que vale numa fórmula. Esta cópia existe
 * para a tela mostrar o nome EXATO enquanto se digita, inclusive com o escape.
 */
export function formulaName(alias: string): string {
    const name = alias
        .replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase())
        .replace(/-([0-9])/g, '_$1')
    if (/^[0-9]/.test(name)) return `_${name}`
    if (FORMULA_RESERVED.has(name)) return `${name}_`
    return name
}

/** A mesma lista do `RESERVED_NAMES` do servidor — ver lá o porquê de cada família. */
const FORMULA_RESERVED: ReadonlySet<string> = new Set([
    'true', 'false', 'null', 'in', 'as', 'break', 'const', 'continue', 'else', 'for', 'function',
    'if', 'import', 'let', 'loop', 'package', 'namespace', 'return', 'var', 'void', 'while',
    'int', 'uint', 'double', 'bool', 'string', 'bytes', 'list', 'map', 'type', 'dyn',
    'timestamp', 'duration', 'has', 'size', 'matches', 'cel', 'optional',
    'filled', 'empty', 'daysBetween', 'asList',
    'self', 'today', 'now', 'state', 'currentUser'
])

const SAFE_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:', 'ftp:']
const SCHEME = /^([a-zA-Z][a-zA-Z0-9+.-]*):/
const PORT_AFTER_COLON = /^\d{1,5}(?:[/?#]|$)/
// control chars are matched on purpose: browsers strip them from href, so
// "java\u0000script:" would slip past a scheme check that ignored them
// eslint-disable-next-line no-control-regex
const UNSAFE_CHARS = /[\s<>"'\\`\u0000-\u001f\u007f]/
// host without a scheme: labels ending in an alphabetic TLD, so that a plain
// decimal like "1.5" is not read as the IPv4 address 1.0.0.5
const LABEL = '[a-z0-9\\u00a1-\\uffff](?:[a-z0-9\\u00a1-\\uffff-]*[a-z0-9\\u00a1-\\uffff])?'
const HOSTLIKE = new RegExp(
    '^' + LABEL + '(?:\\.' + LABEL + ')*\\.[a-z\\u00a1-\\uffff]{2,}(?::\\d{1,5})?(?:[/?#].*)?$', 'i')
const EMAIL = /^[^@/:]+@[a-z0-9-]+(?:\.[a-z0-9-]+)+$/i

/**
 * Normalizes a stored value into a safe href, or null when the value is not
 * something we are willing to render as a link. Callers fall back to plain
 * text on null, so this doubles as the validation rule for the url subtype.
 */
export function urlHref(value: any): string | null {
    if (isNullOrUndefined(value)) return null
    const raw = value.toString().trim()
    if (!raw || UNSAFE_CHARS.test(raw)) return null

    const scheme = raw.match(SCHEME)
    // "host.com:8443/x" also matches SCHEME (dots are legal scheme chars), so a
    // colon followed by a port means we are still looking at a schemeless host
    const hasScheme = !!scheme && !PORT_AFTER_COLON.test(raw.slice(scheme[0].length))

    let candidate: string
    if (hasScheme) {
        // explicit scheme: allowlist only, so javascript:, data:, file: are rejected
        if (SAFE_PROTOCOLS.indexOf(scheme![1].toLowerCase() + ':') < 0) return null
        candidate = raw
    } else if (raw.indexOf('//') === 0) {
        candidate = 'https:' + raw
    } else if (EMAIL.test(raw)) {
        candidate = 'mailto:' + raw
    } else if (HOSTLIKE.test(raw)) {
        candidate = 'https://' + raw
    } else {
        return null
    }

    try {
        const url = new URL(candidate)
        if (SAFE_PROTOCOLS.indexOf(url.protocol.toLowerCase()) < 0) return null
        if ((url.protocol === 'http:' || url.protocol === 'https:') && !url.hostname) return null
        return url.href
    } catch (e) {
        return null
    }
}

export default { aliasSuggestion, incompleteAliasSuggestion, isNullOrUndefined, urlHref, asBoolean }
